import { useAppDispatch, useAppSelector } from '@backrs_ui/grow-ui/src/redux/hooks';
import {
  getSessionState,
  getActiveLubavSession,
  createSessionAction,
} from '@backrs_ui/grow-ui/src/redux/lubavSessionState/lubavSessionState.utils';
import {
  setUserInput,
  setUserMessage,
} from '@backrs_ui/grow-ui/src/redux/solutionSession/solutionSession';

import { Box, TextField, InputAdornment, Theme, SxProps } from '@mui/material';

type MultiFreeformProps = Readonly<{
  options: string[] | undefined;
  sendQAMessage: (message: string, LLMInput?: string) => void;
  numericOnly?: boolean;
}>;

export const MultiFreeform = ({
  options,
  sendQAMessage,
  numericOnly = false,
}: MultiFreeformProps) => {
  const dispatch = useAppDispatch();
  const activeSession = useAppSelector(getActiveLubavSession);
  const { userInput } = useAppSelector(
    (state) => getSessionState(state, activeSession).solutionSession,
  );

  if (!activeSession) {
    return null;
  }

  if (userInput?.type !== 'MULTIPLE_INPUT_MAP') {
    dispatch(
      createSessionAction(setUserInput, activeSession)({ type: 'MULTIPLE_INPUT_MAP', value: {} }),
    );

    return null;
  }

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();

      if (Object.keys(userInput.value).length === 0) {
        return;
      }

      const formattedObject = Object.entries(userInput.value)
        .map(([key, value]) => `${key}: ${value}`)
        .join(', ');

      sendQAMessage(formattedObject, formattedObject);

      dispatch(
        createSessionAction(setUserInput, activeSession)({ type: 'MULTIPLE_INPUT_MAP', value: {} }),
      );

      dispatch(createSessionAction(setUserMessage, activeSession)(''));
    }
  };

  const handleInputChange = (option: string, value: string) => {
    const newValue = numericOnly ? value.replace(/[^0-9]/g, '') : value;
    const newInput = { ...userInput.value, [option]: newValue };

    dispatch(
      createSessionAction(
        setUserInput,
        activeSession,
      )({ type: 'MULTIPLE_INPUT_MAP', value: newInput }),
    );

    dispatch(
      createSessionAction(
        setUserMessage,
        activeSession,
      )(
        Object.entries(newInput)
          .map(([key, value]) => `${key}: ${value}`)
          .join('\n\n'),
      ),
    );
  };

  return (
    <Box sx={{ mb: 2 }}>
      {options?.map((option, index) => (
        <Box key={option} sx={freeformContainerSx}>
          <TextField
            id={`multifreeform-input-${index}`}
            fullWidth
            value={userInput.value[option] || ''}
            onKeyPress={handleKeyPress}
            onChange={(e) => handleInputChange(option, e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start" sx={customStartAdornment(!!option)}>
                  {option}
                </InputAdornment>
              ),
              ...(numericOnly && {
                inputProps: { inputMode: 'numeric', pattern: '[0-9]*' },
              }),
            }}
            sx={textFieldStyle(false)}
          />
        </Box>
      ))}
    </Box>
  );
};

const freeformContainerSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  mb: backrs.spacing['spacing-md'],
  width: '50%',
});

const customStartAdornment =
  (hasLabel: boolean): SxProps<Theme> =>
  ({ backrs }) => ({
    alignItems: 'center',
    paddingRight: backrs.spacing['spacing-md'],
    maxWidth: { xs: '50%', md: '175px' },
    width: { md: '175px' },
    borderRight: `1px solid ${backrs.colors['Colors/Border/border-primary']}`,
    maxHeight: 'unset',
    height: backrs.spacing['spacing-5xl'],
    display: {
      xs: hasLabel ? 'none' : 'flex',
      sm: 'flex',
    },
    '& > *': {
      textOverflow: 'ellipsis',
      overflow: 'hidden',
      whiteSpace: 'nowrap',
      width: '100%',
    },
  });

const textFieldStyle =
  (isLastItem: boolean): SxProps<Theme> =>
  ({ backrs }) => ({
    mb: {
      xs: backrs.spacing['spacing-md'],
      sm: backrs.spacing[isLastItem ? 'spacing-md' : 'spacing-2xl'],
    },
    width: '100%',
    '& .MuiInputBase-adornedStart': {
      paddingY: `${backrs.spacing['spacing-none']} !important`,
    },
    '& .MuiInputBase-root': {
      height: '40px !important',
      minHeight: 'unset !important',
    },
    '& .MuiInputBase-input': {
      minWidth: '20px',
    },
    '& .MuiInputAdornment-root': {
      marginRight: 0,
      height: '40px !important',
      minHeight: 'unset !important',
      '& > *': {
        width: 'auto',
        height: 'auto',
      },
    },
  });
